import {Injectable} from '@angular/core';
import {
  Contact,
  InsuranceService,
  ObjectConverterUtil,
  Ref,
  Ticket,
  TicketInsuranceMandateRequest,
  TicketInsuranceMandateRequestService,
  TicketService,
  TicketStatus,
  TicketType,
  ValidationResult,
  TicketContact,
  TicketContactService,
  TicketContactType,
  SimplePaginationUtils,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, concat, forkJoin, Observable, of, Subject, throwError} from 'rxjs';
import {debounceTime, filter, last, map, publishReplay, refCount, switchMap, tap, toArray} from 'rxjs/operators';
import {CustomerContextService} from './customer-context-service';
import {isSameRef} from './utils';
import {PendingTicketDraftService} from './pending-ticket-draft.service';
import {OfflineInsuranceMandateRequest} from '../mandate-route/offline-insurance-mandate-request';
import {TicketContactSearch} from '@lifeislife/lifeislife-domain';
import {SearchResult} from '@lifeislife/lifeislife-domain';
import {TicketSearch} from '@lifeislife/lifeislife-domain';

@Injectable({
  providedIn: 'root',
})
export class CustomerInsuranceMandateRequestHelperService {

  editingTicket$ = new BehaviorSubject<OfflineInsuranceMandateRequest | null>(null);
  nextDraftMandateRequest$ = new BehaviorSubject<TicketInsuranceMandateRequest>(null);
  nextDraftMandateRequestValidationResults$: Observable<ValidationResult<TicketInsuranceMandateRequest>>;

  committingDraft$ = new BehaviorSubject<boolean>(false);
  committedDrafts$ = new Subject<Ref<Ticket>>();

  constructor(
    private customerContextService: CustomerContextService,
    private ticketService: TicketService,
    private ticketContactService: TicketContactService,
    private insuranceService: InsuranceService,
    private pendingTicketDraftService: PendingTicketDraftService,
    private ticketInsuranceMandateRequestService: TicketInsuranceMandateRequestService,
  ) {
    this.nextDraftMandateRequestValidationResults$ = this.nextDraftMandateRequest$.pipe(
      debounceTime(200),
      switchMap(r => this.validateRequest$(r)),
      publishReplay(1), refCount(),
    );
  }

  createOfflineRequest() {
    const contactRef = this.customerContextService.getActiveContactRef();
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const subject = this.getNewTicketSubject();
    const ticket = {
      subject: subject,
      authorContactRef: contactRef,
      ticketType: TicketType.INSURANCE_MANDATE_REQUEST,
      ticketStatus: TicketStatus.DRAFT,
    } as Ticket;

    this.editingTicket$.next({
      customerRef: customerRef,
      contactRef: contactRef,
      ticket: ticket,
      mandateRequests: [],
    });
    this.nextDraftMandateRequest$.next({
      customerSubscriber: true,
    } as TicketInsuranceMandateRequest);
    return;
  }

  cancelOfflineRequest() {
    if (this.committingDraft$.getValue()) {
      console.warn(`Commit in progress`);
      return;
    }
    this.editingTicket$.next(null);
    this.nextDraftMandateRequest$.next(null);
  }

  setNextDraftRequest(request: TicketInsuranceMandateRequest) {
    const editingTicket = this.editingTicket$.getValue();
    if (editingTicket == null) {
      console.warn(`No ticket`);
      return;
    }
    // Add current mandate request to ticket list
    if (this.nextDraftMandateRequest$.getValue()) {
      const prevRequest = this.nextDraftMandateRequest$.getValue();
      editingTicket.mandateRequests.push(prevRequest);
    }
    // Remove new mandate request from ticket list
    editingTicket.mandateRequests = editingTicket.mandateRequests.filter(m => m !== request);
    this.nextDraftMandateRequest$.next(request);
  }

  getNextDraftRequest() {
    return this.nextDraftMandateRequest$.getValue();
  }

  cancelNextDraftRequest() {
    this.nextDraftMandateRequest$.next(null);
  }

  cancelDraftRequest(request: TicketInsuranceMandateRequest) {
    if (this.nextDraftMandateRequest$.getValue() === request) {
      this.cancelNextDraftRequest();
      return;
    }
    if (this.editingTicket$.getValue()) {
      const offlineTicket = this.editingTicket$.getValue();
      offlineTicket.mandateRequests = offlineTicket.mandateRequests.filter(r => r !== request);
    }
  }

  addNextDraftRequest() {
    this.setNextDraftRequest({
      customerSubscriber: true,
    } as TicketInsuranceMandateRequest);
  }

  commitDraft$(): Observable<Ticket> {
    if (this.committingDraft$.getValue()) {
      console.warn(`Already committing`);
      return;
    }
    this.committingDraft$.next(true);

    const ticketRequest = this.editingTicket$.getValue();
    if (this.nextDraftMandateRequest$.getValue()) {
      ticketRequest.mandateRequests.push(this.nextDraftMandateRequest$.getValue());
      this.nextDraftMandateRequest$.next(null);
    }


    return this.createTicket$(ticketRequest).pipe(
      switchMap(t => this.createMandateRequests$(t, ticketRequest)),
      switchMap(t => this.commitTicket$(t)),
      tap(t => {
        this.committingDraft$.next(false);
        this.committedDrafts$.next(t);
      }, e => {
        this.pendingTicketDraftService.reload();
        this.committingDraft$.next(false);
      }),
    );
  }

  getCommitted$(ref: Ref<Ticket>) {
    return this.committedDrafts$.pipe(
      filter(r => isSameRef(r, ref)),
      publishReplay(1), refCount(),
    );
  }

  private getNewTicketSubject() {
    return `Changement d'intermédiaire`; // TODO i18n
  }

  private validateRequest$(request: TicketInsuranceMandateRequest) {
    if (request == null) {
      return of(null);
    }
    return this.ticketInsuranceMandateRequestService.validateTicketInsuranceMandateRequest$(request).pipe(
      map(r => ObjectConverterUtil.filterValidationResult(r, 'ticketRef')),
      map(r => {
        if (request.insuranceCategoryRef == null && request.providerCategoryName == null) {
          r.valid = false;
          // r.propertiesErrors['insuranceCategoryRef'] = [`Veuillez spécifier le type de police`];
        }
        return r;
      }),
    );
  }

  private createTicket$(ticketRequest: OfflineInsuranceMandateRequest): Observable<Ticket> {
    const ticket: Ticket = ticketRequest.ticket;

    return this.ticketService.saveTicket$(ticket).pipe(
      switchMap(ref => this.createTicketCustomer$(ref, ticketRequest)),
      switchMap(ref => this.ticketService.getTicket$(ref)),
    );
  }

  private createTicketCustomer$(ticketRef: Ref<Ticket>, ticketRequest: OfflineInsuranceMandateRequest): Observable<Ref<Ticket>> {
    return this.ticketService.addTicketCustomer$(ticketRef, ticketRequest.customerRef).pipe(
      map(a => ticketRef),
    );
  }

  private createMandateRequests$(ticket: Ticket, ticketRequest: OfflineInsuranceMandateRequest) {
    const ticketContactSearch: TicketContactSearch = {
      ticketSearch: {
        exactTicketRef: {id: ticket.id},
      } as TicketSearch,
      contactSearch: {
        exactContactRef: ticketRequest.contactRef,
      },
    } as TicketContactSearch;
    const ticketContact$: Observable<TicketContact> = this.ticketContactService
      .searchTicketContacts$(ticketContactSearch, SimplePaginationUtils.newPagination(15)).pipe(
        switchMap(r => this.getOrCreateTicketContact$(r, ticket, ticketRequest)),
      );

    return ticketContact$.pipe(
      switchMap(tc => this.createMandateRequestsWithTicketContact$(ticket, tc, ticketRequest)),
    );
  }

  private createMandateRequestsWithTicketContact$(ticket: Ticket, ticketContact: TicketContact,
                                                  ticketRequest: OfflineInsuranceMandateRequest) {
    const task$List = ticketRequest.mandateRequests.map(m => this.createMandateRequest$(ticket, ticketContact, m));
    const taskList$ = task$List.length === 0 ? of([]) : concat(...task$List).pipe(toArray());
    return taskList$.pipe(
      map(r => ticket),
    );
  }

  private createMandateRequest$(ticket: Ticket, ticketContact: TicketContact, request: TicketInsuranceMandateRequest) {
    request.ticketRef = {id: ticket.id};
    if (!request.customerSubscriber) {
      request.contactSubscriberRef = {id: ticketContact.id};
    }
    return this.ticketInsuranceMandateRequestService.saveTicketInsuranceMandateRequest$(request);
  }

  private commitTicket$(ticket: Ticket) {
    return this.ticketService.updateTicketStatus$(ticket, TicketStatus.CREATED).pipe(
      switchMap(ref => this.ticketService.getTicket$(ref, true)),
    );
  }

  private getOrCreateTicketContact$(restuls: SearchResult<TicketContact>, ticketRef: Ref<Ticket>,
                                    ticketRequest: OfflineInsuranceMandateRequest) {
    if (restuls.count > 0) {
      return of(restuls.list[0]);
    }
    const newContact: TicketContact = {
      id: null,
      contactRef: ticketRequest.contactRef,
      ticketRef: ticketRef,
      contactType: TicketContactType.PARTICIPANT,
    };
    return this.ticketContactService.saveTicketContact$(newContact).pipe(
      switchMap(ref => this.ticketContactService.getTicketContact$(ref)),
    );
  }
}
